import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import type { FormControl, UntypedFormArray } from '@angular/forms';
import { FEATURE_TOGGLER, FeatureToggler } from '@cbre/feature-toggles';
import deepmerge from 'deepmerge';
import { FloorClass } from '../../../../models/floor.class';
import { SHOW_FLOOR_INDEX_FEATURE_KEY } from '../../../../shared/constants/features.const';
import { isNotNullOrEmptyString } from '../../../../shared/utils/functions';
import { ElevatorBankHeader } from '../../../actions-toolbar/actions-toolbar.model';
import { EDPElevatorBank, ELEVATOR_BANK_HEADERS } from '../../../floor-container/elevator-banks/elevator-banks.model';
import { UpdateStackingPlanInfo } from '../../../floor-container/floor/floor.model';
import { ElevatorBank } from '../../../stacking-plan.model';
import { FloorOption } from './elevators.model';
import { ElevatorsValidatos } from './elevators.validators';
import { Observable, forkJoin, BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, delay, filter, share, startWith, tap } from 'rxjs/operators';
import { ActionsToolbarService } from '../../../actions-toolbar/actions-toolbar.service';
import { LovValues } from '../../../../stacking-plan.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
@UntilDestroy()
@Component({
  selector: 'sp-elevators',
  templateUrl: './elevators.component.html',
  styleUrls: ['./elevators.component.scss'],
  standalone: false,
})
export class ElevatorsComponent implements OnInit {
  @Input() elevatorInfo: ElevatorBank[];
  @Input() floorData: FloorClass[];
  @Output() updateElevatorData: EventEmitter<UpdateStackingPlanInfo> = new EventEmitter();

  elevatorTypes: LovValues[] = [];
  elevatorBankCapacityUOMValues: LovValues[] = [];
  adaCompliantOptions: LovValues[] = [];
  elevatorsFG: UntypedFormGroup;
  floorHeaders: ElevatorBankHeader[] = ELEVATOR_BANK_HEADERS;
  elevators: ElevatorBank[];
  deletedElevators: ElevatorBank[] = [];
  floorsOptions: FloorOption[] = [];

  #loadindData = new BehaviorSubject(true);
  loadindData$ = this.#loadindData.asObservable();

  constructor(
    private fb: UntypedFormBuilder,
    @Inject(FEATURE_TOGGLER) private toggleRouterService: FeatureToggler,
    private actionsToolbarService: ActionsToolbarService
  ) {}

  ngOnInit() {
    this.getElevatorLOVs()
      .pipe(
        // ponytail: one frame is enough for the dialog to paint first; use MatDialogRef.afterOpened() if it ever flickers
        delay(100),
        untilDestroyed(this),
        tap(() => this.#loadindData.next(false)),
        catchError(() => {
          this.#loadindData.next(false);
          return EMPTY;
        })
      )
      .subscribe();
    this.elevators = deepmerge([], this.elevatorInfo);
    this.formInit();
    this.initFloorOptions();
  }

  /**
   * Constructing the elevatorsFG form group and its child form
   * array(elevator)'s form controls
   */
  private formInit() {
    const elevatorTableFGs: UntypedFormGroup[] = this.elevators.map(elevator =>
      this.fb.group({
        elevatorTypeDesc: [elevator.elevatorTypeDesc],
        elevatorBankName: [elevator.elevatorBankName, [Validators.required]],
        // TODO: BASE STOP column need to be added later
        // baseStop: [elevator.baseStop],
        firstStop: [elevator.firstStop],
        lastStop: [elevator.lastStop],
        elevatorBankCapacity: this.fb.group(
          {
            val: [elevator.elevatorBankCapacity],
            uom: [elevator.elevatorBankCapacityUOM],
          },
          { validators: [ElevatorsValidatos.notEmptyUOM] }
        ),
        adaCompliant: [elevator.adaCompliant],
        elevatorBankNotes: [elevator.elevatorBankNotes],
      })
    );
    this.elevatorsFG = this.fb.group({
      elevator: this.fb.array(elevatorTableFGs),
    });

    // subscribe to main inputs uom changes
    this.subscribeToElevatorBankUOM();
  }

  //setting empty when min value is 0 and negative value populated from EDP,
  handleBackspace(event: KeyboardEvent, control: FormControl) {
    if (control.value < 0) {
      control.setValue(null);
      event.preventDefault();
    }
  }

  private initFloorOptions() {
    this.floorsOptions = FloorClass.generateFloorCatalogOptionsFrom(this.floorData, {
      disableIfFloorNumberValue: false,
      useIndexAsValue: true,
      showIndex: this.toggleRouterService.isFeatureEnabled(SHOW_FLOOR_INDEX_FEATURE_KEY),
      showHiddenFloors: false,
      showInvalidFloors: false,
    });
  }

  get elevatorFormArray() {
    return this.elevatorsFG.get('elevator') as UntypedFormArray;
  }

  /** Initialize the new elevator's form group and form controls */
  private initNewElevatorItemGroup() {
    const initFormGroup = this.fb.group({
      elevatorTypeDesc: [{ value: null, disabled: false }],
      elevatorBankName: [{ value: '', disabled: false }, [Validators.required]],
      firstStop: [{ value: '', disabled: false }, [Validators.required]],
      lastStop: [{ value: '', disabled: false }, [Validators.required]],
      elevatorBankCapacity: this.fb.group(
        {
          val: [{ value: null, disabled: false }],
          uom: [{ value: null, disabled: false }],
        },
        { validators: [ElevatorsValidatos.notEmptyUOM] }
      ),
      adaCompliant: [{ value: null, disabled: false }],
      elevatorBankNotes: [{ value: null, disabled: false }],
    });

    this.subscribeToValAndUomLOV(initFormGroup.get('elevatorBankCapacity') as UntypedFormGroup);
    return initFormGroup;
  }

  /**
   * When user clicked on ADD ELEVATOR BANK button, it will add the new
   * elevator data with empty values to the elevatorInfo array at first
   * position and will add new form group to the elevatorsFG-elevator form
   * array at first position
   */
  public addElevatorBank() {
    const newElevatorInfo: ElevatorBank = {
      elevatorBankId: null,
      elevatorTypeDesc: null,
      elevatorBankName: '',
      totalNumberOfFloors: null,
      firstStop: '',
      lastStop: '',
      elevatorBankCapacity: null,
      elevatorBankCapacityUOM: null,
      adaCompliant: null,
      elevatorBankNotes: null,
    };
    this.elevators.push(newElevatorInfo);
    this.elevatorFormArray.push(this.initNewElevatorItemGroup());
  }

  get ElevatorValues() {
    return this.elevatorsFG.value.elevator.map(elevator => {
      return {
        ...elevator,
        elevatorBankCapacity: elevator.elevatorBankCapacity.val,
        elevatorBankCapacityUOM: elevator.elevatorBankCapacity.uom,
      };
    });
  }

  private subscribeToElevatorBankUOM() {
    const controls = this.elevatorFormArray.controls;
    controls.forEach((control: UntypedFormGroup) => {
      const elevatorBankCapacity: UntypedFormGroup = control.controls.elevatorBankCapacity as UntypedFormGroup;
      this.subscribeToValAndUomLOV(elevatorBankCapacity);
    });
  }

  private subscribeToValAndUomLOV(valueUomGroup: UntypedFormGroup) {
    const elevatorBankCapacity: UntypedFormGroup = valueUomGroup as UntypedFormGroup;
    const elevatorBankCapacityUomValue: string = (elevatorBankCapacity.value.uom as string) || null;
    const valControl: UntypedFormControl = valueUomGroup.controls.val as UntypedFormControl;
    const uomControl: UntypedFormControl = valueUomGroup.controls.uom as UntypedFormControl;

    valControl.valueChanges
      .pipe(
        untilDestroyed(this),
        startWith(valControl.value),
        filter(() => {
          return (
            isNotNullOrEmptyString(valControl.value) &&
            !isNotNullOrEmptyString(uomControl.value) &&
            this.elevatorBankCapacityUOMValues != null &&
            this.elevatorBankCapacityUOMValues.length > 0
          );
        })
      )
      .subscribe(() => {
        uomControl.setValue(elevatorBankCapacityUomValue || this.elevatorBankCapacityUOMValues[0].key);
      });
  }

  /**
   * When user clicks on save button in elevator component, the elevator changes
   * will update in updateElevatorData array. From here we are sending the
   * updateElevatorData in actual json format to parent component(more info
   * dialog) through output event emitter
   */
  public saveElevatorInfo() {
    const updateElevatorData: EDPElevatorBank[] = this.elevators.map((elevator, index) => {
      const elevatorsValues: ElevatorBank[] = this.ElevatorValues;
      return {
        id: elevator.elevatorBankId,
        elevator_type_desc: elevatorsValues[index].elevatorTypeDesc,
        elevator_bank_name: elevatorsValues[index].elevatorBankName,
        total_number_of_elevators: elevator.totalNumberOfFloors,
        start_floor: elevatorsValues[index].firstStop,
        end_floor: elevatorsValues[index].lastStop,
        elevator_bank_capacity: elevatorsValues[index].elevatorBankCapacity,
        elevator_bank_capacity_uom: elevatorsValues[index].elevatorBankCapacity
          ? elevatorsValues[index].elevatorBankCapacityUOM
          : null,
        ada_compliant_f: elevatorsValues[index].adaCompliant,
        elevator_bank_notes: elevatorsValues[index].elevatorBankNotes,
      };
    });
    this.updateElevatorData.emit({ elevatorBank: updateElevatorData, deletedElevators: this.deletedElevators });
  }

  /*
   * It will checks the whether first stop is smaller than last stop or not
   * Whenever user has selects first stop value greater then last stop then it sets incorrect error
   */
  public changeStop(elevatorIndex: number) {
    const elevatorBankRowFG = this.elevatorFormArray.controls[elevatorIndex] as UntypedFormGroup;
    const firstStopFC = elevatorBankRowFG.get('firstStop') as UntypedFormControl;
    const lastStopFC = elevatorBankRowFG.get('lastStop') as UntypedFormControl;

    const firstStopValue = this.getNumericFloorValue(firstStopFC.value);
    const lastStopValue = this.getNumericFloorValue(lastStopFC.value);
    const validateFirstStopValue = isNotNullOrEmptyString(firstStopFC.value);
    const validateLastStopValue = isNotNullOrEmptyString(lastStopFC.value);
    if (validateFirstStopValue && validateLastStopValue && firstStopValue >= lastStopValue) {
      firstStopFC.setErrors({ incorrect: true });
    } else {
      firstStopFC.setErrors(null);
    }
  }

  private getNumericFloorValue(value: string | number): number {
    const numericValue = Number(value);
    let numberToReturn = numericValue;
    if (Number.isNaN(numericValue) && typeof value !== 'number') {
      const strFloorOption = this.floorsOptions.find(floorOption => floorOption.value === value);
      numberToReturn = strFloorOption.altValue;
    }
    return numberToReturn as number;
  }

  /*
   * It will push the deleted elevator into new array @deletedElevators, if its
   * already exists(new elevator will not have elevator bank id)
   */
  public deleteElevator(elevatorIndex: number, elevator: ElevatorBank) {
    this.elevatorFormArray.removeAt(elevatorIndex);
    this.elevatorsFG.markAsDirty();
    this.elevatorsFG.markAsTouched();

    this.elevators.splice(elevatorIndex, 1);
    if (!!elevator.elevatorBankId) {
      this.deletedElevators.push(elevator);
    }
  }

  /**
   * Request and assign elevator type and elevator bank LOV
   */
  private getElevatorLOVs(): Observable<LovValues[][]> {
    return forkJoin([
      this.actionsToolbarService.getElevatorBankCapacityLOVs().pipe(
        filter(Boolean),
        tap(elevatorBankCapacityLOV => {
          this.elevatorBankCapacityUOMValues = elevatorBankCapacityLOV;
        })
      ),
      this.actionsToolbarService.getElevatorTypes().pipe(
        filter(Boolean),
        tap(elevatorTypes => {
          this.elevatorTypes = elevatorTypes;
        })
      ),
      this.actionsToolbarService.getADACompliants().pipe(
        filter(Boolean),
        tap(adaCompliants => {
          this.adaCompliantOptions = adaCompliants.map(
            adaCompliant =>
              ({
                key: adaCompliant.key,
                value: adaCompliant.value === 'Y' ? 'YES' : 'NO',
              }) as LovValues
          );
        })
      ),
    ]).pipe(share());
  }
}
